import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
} from "react-router-dom";
import {
  Menu,
  MessageSquare,
  X,
  Briefcase,
  User,
  BookOpen,
  Home,
  PlusCircle,
  LogOut,
} from "lucide-react";

// Import all components
import JobSearch from "./Views/JobSearch";
import JobPostingForm from "./Views/JobPostForm";
import SignInPage from "./Views/Signin";
import SignUpPage from "./Views/SignUpPage";
import JobListings from "./Views/JobListing";
import JobApplicationForm from "./Views/JobApplicationForm";
import Chat from "./Views/Chat";
import ProfilePage from "./Views/ProfilePage";
import CoursesPage from "./Views/CoursesPage";
import Logo from "./Assets/2-removebg-preview (1) (1).png";

const MainApp = () => {
  const [jobData, setJobData] = useState(null);
  const [allJobPostings, setAllJobPostings] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !!sessionStorage.getItem("authToken");
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleFormSubmit = (data) => {
    setJobData(data);
    setAllJobPostings((prevPostings) => [...prevPostings, data]);
  };

  const handleSubmitAll = (postings) => {
    console.log("All job postings submitted:", postings);
  };

  const handleLogin = (token) => {
    sessionStorage.setItem("authToken", token);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("userId");
    setIsLoggedIn(false);
  };

  const ProtectedRoute = ({ children }) => {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      return <Navigate to="/signin" replace />;
    }
    return children;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <nav className="bg-gradient-to-r from-[#24786D] to-[#1a5a52] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              {/* Logo and Desktop Navigation */}
              <div className="flex items-center flex-1">
                {/* Logo */}

                <div className="h-16 w-auto">
                  {" "}
                  {/* Adjust height as needed */}
                  <img
                    src={Logo}
                    alt="JobBoard Logo"
                    className="h-full w-auto object-contain"
                  />
                </div>

                {/* Desktop Navigation Links */}
                <div className="hidden md:flex md:space-x-4">
                  <Link
                    to="/jobs"
                    className="text-gray-100 inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md"
                  >
                    <Briefcase className="w-5 h-5 mr-2" />
                    Jobs
                  </Link>
                  <Link
                    to="/search"
                    className="text-gray-100 inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md"
                  >
                    <Home className="w-5 h-5 mr-2" />
                    Home
                  </Link>
                  <Link
                    to="/chat"
                    className="text-gray-100 inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Chat
                  </Link>
                  <Link
                    to="/courses"
                    className="text-gray-100 inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md"
                  >
                    <BookOpen className="w-5 h-5 mr-2" />
                    Courses
                  </Link>
                  <Link
                    to="/profile"
                    className="text-gray-100 inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md"
                  >
                    <User className="w-5 h-5 mr-2" />
                    Profile
                  </Link>
                </div>

                {/* Right Side Actions */}
                <div className="hidden md:flex md:items-center md:space-x-4 ml-auto">
                  {isLoggedIn ? (
                    <>
                      <Link
                        to="/post"
                        className="text-gray-100 inline-flex items-center px-4 py-2 text-sm font-medium transition-all duration-300 hover:bg-white/10 rounded-md border border-transparent hover:border-white/20"
                      >
                        <PlusCircle className="w-5 h-5 mr-2" />
                        Post a Job
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-white inline-flex items-center hover:bg-white/10 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 border border-transparent hover:border-white/20"
                      >
                        <LogOut className="w-5 h-5 mr-2" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/signin"
                      className="text-white hover:bg-white/10 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 border border-transparent hover:border-white/20"
                    >
                      Sign In
                    </Link>
                  )}
                </div>
              </div>

              {/* Mobile menu button */}
              {/* Mobile menu button - Hidden on desktop */}
              <div className="block md:hidden">
                {" "}
                {/* This ensures it's hidden on md screens and up */}
                <button
                  onClick={toggleMobileMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 transition-all duration-300"
                  aria-expanded="false"
                >
                  {isMobileMenuOpen ? (
                    <X className="block h-6 w-6" />
                  ) : (
                    <Menu className="block h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            className={`md:hidden transition-all duration-300 ease-in-out ${
              isMobileMenuOpen ? "max-h-96" : "max-h-0"
            } overflow-hidden`}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-gradient-to-b from-[#1a5a52] to-[#24786D]">
              <Link
                to="/jobs"
                className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Briefcase className="w-5 h-5 inline-block mr-2" />
                Jobs
              </Link>
              <Link
                to="/search"
                className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Home className="w-5 h-5 inline-block mr-2" />
                Home
              </Link>
              <Link
                to="/chat"
                className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <MessageSquare className="w-5 h-5 inline-block mr-2" />
                Chat
              </Link>
              <Link
                to="/courses"
                className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <BookOpen className="w-5 h-5 inline-block mr-2" />
                Courses
              </Link>
              <Link
                to="/profile"
                className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <User className="w-5 h-5 inline-block mr-2" />
                Profile
              </Link>
              {isLoggedIn && (
                <>
                  <Link
                    to="/post"
                    className="text-white block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <PlusCircle className="w-5 h-5 inline-block mr-2" />
                    Post a Job
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-white/10"
                  >
                    <LogOut className="w-5 h-5 inline-block mr-2" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <JobSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signin"
            element={<SignInPage onLogin={handleLogin} />}
          />
          <Route path="/signup" element={<SignUpPage />} />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <JobListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <JobSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <CoursesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post"
            element={
              <ProtectedRoute>
                <div className="bg-gray-50 min-h-screen">
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <JobPostingForm
                      onAdd={handleFormSubmit}
                      onSubmitAll={handleSubmitAll}
                    />
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/apply/:jobId"
            element={
              <ProtectedRoute>
                <JobApplicationForm />
              </ProtectedRoute>
            }
          />
          {/* Catch-all redirect to home */}
          <Route path="*" element={<Navigate to="/jobs" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default MainApp;
